import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClientConsumptionSummary, computeAnnualVolumeM3, getReferenceConsumption } from '../../utils/consumptionCalculations.js';
import { FaTint, FaUserAlt, FaLayerGroup, FaMapMarkerAlt, FaBuilding, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import UsageDonutChartNivo from '../dashboardComponents/UsageDonutChartNivo.js';
import UsageDonutExportView from '../dashboardComponents/UsageDonutExportView.js';
import SegmentedToggle from '../shared/SegmentedToggle.js';
import { generateLegendData, CONSUMPTION_GRADIENT, colorsAlongGradient, foldIntoOthers } from '../shared/chartTheme.js';
import ConsumptionBreakdownDetail from '../dashboardComponents/ConsumptionBreakdownDetail.js';
import CostPerUsageCard from '../dashboardComponents/CostPerUsageCard.js';
import ConsumptionPerPersonCard from '../dashboardComponents/ConsumptionPerPersonCard.js';
import RatePerEquipment from '../dashboardComponents/RatePerEquipment.js';
import RatePerRoomCard from '../dashboardComponents/RatePerRoomCard.js';
import SavingsPerMeasure from '../dashboardComponents/SavingsPerMeasure.js';
import Gauge from '../dashboardComponents/Gauge.js';
import WalterLoader from '../shared/WalterLoader';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/ClientDashboard.css';
import ReportFolderPickerModal from '../shared/ReportFolderPickerModal.js';
import WaterCostBreakdown from '../dashboardComponents/WaterCostBreakdown.js';
import UsageTaxonomySunburstCard from '../dashboardComponents/UsageTaxonomySunburstCard.js';
import UsageTaxonomySunburstReportCard from '../dashboardComponents/UsageTaxonomySunburstReportCard.js';
import { useUsageTaxonomy } from '../shared/useUsageTaxonomy.js';
import { useAppMetadata } from '../shared/useAppMetadata.js';
import { getEffectiveHotWaterRatio } from './actionPlan/calculations/waterPricing.js';

// Part du total du bâtiment sous laquelle une pièce rejoint « Autres pièces » de sa section.
const ROOM_FOLD_SHARE = 0.02;

// Niveau d'un risque Géorisques (buildings.risks.<clé>) : statut à l'adresse s'il est
// connu, sinon celui de la commune. Faible / non concerné → vert, existant / modéré →
// jaune, important → rouge, pas de donnée ou statut inconnu → gris.
const RISK_LEVELS = {
  important: { label: 'Important', color: 'red', order: 0 },
  moderate: { label: 'Modéré', color: 'yellow', order: 1 },
  existing: { label: 'Existant', color: 'yellow', order: 2 },
  low: { label: 'Faible', color: 'green', order: 3 },
  none: { label: 'Non concerné', color: 'green', order: 4 },
  unknown: { label: 'Inconnu', color: 'grey', order: 5 },
};

const riskLevel = (risk) => {
  if (!risk || (risk.present !== true && risk.present !== false)) return 'unknown';
  if (risk.present === false) return 'none';
  const isUnknown = (statut) => !statut || /inconnu|non connu/i.test(statut);
  const statut = [risk.libelleStatutAdresse, risk.libelleStatutCommune].find((s) => !isUnknown(s));
  if (!statut) return 'existing'; // présent sans précision de niveau (anciennes données)
  if (/important/i.test(statut)) return 'important';
  if (/mod[ée]r[ée]/i.test(statut)) return 'moderate';
  if (/faible/i.test(statut)) return 'low';
  return 'existing';
};

const ClientBuildingDashboard = () => {
  const { client_id, building_id } = useParams();
  const navigate = useNavigate();
  const [dataConsumption, setDataConsumption] = useState(null);
  const [loadingRisques, setLoadingRisques] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('functional_unit');
  const [showReportPicker, setShowReportPicker] = useState(false);
  const [legendData, setLegendData] = useState([]);
  const [dataType, setDataType] = React.useState("real"); // "real" ou "estimated"
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [chartMode, setChartMode] = useState('interactive'); // 'interactive' | 'export'
  const [sunburstMode, setSunburstMode] = useState('interactive'); // 'interactive' | 'export'
  const [roomSunburstMode, setRoomSunburstMode] = useState('interactive'); // 'interactive' | 'export'
  const [waterPointRows, setWaterPointRows] = useState([]);
  const [waterPointsLoading, setWaterPointsLoading] = useState(true);
  const [waterPointsError, setWaterPointsError] = useState(null);
  const { categories: taxonomyCategories, subcategoriesByCategory } = useUsageTaxonomy(building_id);
  const { data: buildingTypeOptions } = useAppMetadata('building_types');

  function calculateWaterCosts(costPerUse) {
    let hotWaterCost = 0;
    let coldWaterCost = 0;

    for (const usage in costPerUse) {
      if (costPerUse.hasOwnProperty(usage)) {
        hotWaterCost += costPerUse[usage].hot_water || 0;
        coldWaterCost += costPerUse[usage].cold_water || 0;
      }
    }
    return {
      hotWaterCost,
      coldWaterCost,
      totalCost: hotWaterCost + coldWaterCost,
    };
  }

  const buildingTypeLabels = useMemo(
    () => Object.fromEntries(buildingTypeOptions.map((o) => [o.value, o.label])),
    [buildingTypeOptions]
  );

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return '-';
    }
    return value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  // Extrait un message d'erreur exploitable d'une réponse HTTP en échec : le
  // backend renvoie parfois du JSON ({"error": "..."}), parfois une page
  // d'erreur HTML brute (ex: exception non interceptée) qui ne se parse pas.
  const parseErrorResponse = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      if (data && data.error) return data.error;
    } catch (e) {
      // Réponse non-JSON (page d'erreur HTML par ex.) : on garde le fallback
    }
    return `${fallbackMessage} (HTTP ${response.status})`;
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const aggregateVolumeByFunctionalUnit = (data, type = "real") => {
    if (!data?.consumption_per_unit) return {};

    const totals = {};
    Object.entries(data.consumption_per_unit).forEach(([unit, values]) => {
      totals[unit] = values[type] || 0; // prend "real" ou "estimated"
    });

    return totals;
  };

  // pour aller chercher les données des graphiques via le back
  useEffect(() => {
    if (building_id) {
      const fetchConsumptionData = async () => {
        const endpoint = `${process.env.REACT_APP_API_URL}/get_json_for_report/${building_id}`;

        fetch(endpoint)
          .then(async (response) => {
            if (!response.ok) {
              throw new Error(await parseErrorResponse(response, "Erreur lors de la récupération des données du bâtiment"));
            }
            return response.json();
          })
          .then((data) => {
            setDataConsumption(data);
            setLoading(false);
          })
          .catch((err) => {
            setError(err.message);
            setLoading(false);
          });
      };

      fetchConsumptionData();
    }
  }, [building_id]);

  // pour aller chercher les points d'eau (équipements x usages) et permettre le détail par clic sur le camembert
  // le volume par usage n'existe nulle part côté back : on le recalcule ici avec la même formule que
  // water_volume_computation.py (miroir déjà maintenu dans consumptionCalculations.js, cf. ClientUsages.js)
  const [rawWaterPoints, setRawWaterPoints] = useState([]);

  useEffect(() => {
    if (!building_id) return;

    setWaterPointsLoading(true);
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/load_equipments_with_rating/${building_id}`).then(async (res) => {
        if (!res.ok) throw new Error(await parseErrorResponse(res, "Erreur lors de la récupération des équipements"));
        return res.json();
      }),
      fetch(`${process.env.REACT_APP_API_URL}/load_usages_by_building_id/${building_id}`).then(async (res) => {
        if (!res.ok) throw new Error(await parseErrorResponse(res, "Erreur lors de la récupération des usages"));
        return res.json();
      }),
    ])
      .then(([equipments, usages]) => {
        const equipmentById = {};
        equipments.forEach((equipment) => {
          equipmentById[equipment._id] = equipment;
        });

        const rows = usages
          .map((usage) => {
            const equipment = equipmentById[usage._id_equipement];
            if (!equipment) return null;
            return {
              equipment_id: equipment._id,
              type_equipment: equipment.type_equipment,
              type_usage: usage.type_usage,
              usage_category_id: usage.usage_category_id,
              usage_subcategory_id: usage.usage_subcategory_id,
              functional_unit: usage.functional_unit,
              hot_water_ratio: usage.hot_water_ratio,
              hot_water_connected: equipment.technical_data?.hot_water,
              equipment_hot_water_ratio: equipment.technical_data?.hot_water_ratio,
              name_section: usage.name_section,
              name_room: usage.name_room,
              // clés de regroupement de « Répartition par section et pièce » (les noms ne sont
              // pas uniques : deux sections peuvent avoir une pièce « WC »)
              section_key: equipment.location?.section_id || 'unknown-section',
              room_key: equipment.location?.room_id || `${equipment.location?.section_id || 'unknown-section'}--unknown-room`,
              room_multiplier: equipment.location?.room_multiplier ?? 1,
              estimated_annual_volume: computeAnnualVolumeM3(equipment, usage),
              rating_letter: equipment.rating_letter,
              nb_equipment: equipment.technical_data?.nb_equipment,
            };
          })
          .filter(Boolean);

        setRawWaterPoints(rows);
        setWaterPointsLoading(false);
      })
      .catch((err) => {
        setWaterPointsError(err.message);
        setWaterPointsLoading(false);
      });
  }, [building_id]);

  // applique le même ratio "réel / estimé" que celui utilisé côté back (report.py: real_ratio)
  useEffect(() => {
    const estimatedTotal = Object.values(dataConsumption?.consumption_per_use || {})
      .reduce((sum, v) => sum + (v.total_consumption_estimated || 0), 0);
    const realTotal = getReferenceConsumption(dataConsumption?.global_water_data);
    const realRatio = estimatedTotal ? (realTotal || estimatedTotal) / estimatedTotal : 1;

    setWaterPointRows(
      rawWaterPoints.map((row) => ({
        ...row,
        annual_volume: dataType === 'real' ? row.estimated_annual_volume * realRatio : row.estimated_annual_volume,
      }))
    );
  }, [rawWaterPoints, dataConsumption, dataType]);

  // Répartition par catégorie / sous-catégorie d'usage (nouvelle taxonomie).
  // Le total d'une catégorie est reconstruit comme la somme de ses propres
  // sous-catégories affichées (plutôt que lu indépendamment) pour garantir que
  // les deux anneaux du donut imbriqué restent alignés angulairement, même si
  // un usage pointe vers une sous-catégorie désactivée/renommée entre-temps.
  const { categoryTotals, subcategoryTotals } = useMemo(() => {
    const categoryVolumeById = {};
    const subcategoryVolumeById = {};
    let uncategorizedVolume = 0;

    waterPointRows.forEach((row) => {
      const volume = row.annual_volume || 0;
      if (!row.usage_category_id || !row.usage_subcategory_id) {
        uncategorizedVolume += volume;
        return;
      }
      categoryVolumeById[row.usage_category_id] = (categoryVolumeById[row.usage_category_id] || 0) + volume;
      subcategoryVolumeById[row.usage_subcategory_id] = (subcategoryVolumeById[row.usage_subcategory_id] || 0) + volume;
    });

    const categoryTotals = [];
    const subcategoryTotals = [];

    taxonomyCategories.forEach((cat) => {
      const subsForThisCategory = [];
      (subcategoriesByCategory[cat._id] || []).forEach((sub) => {
        const subVolume = subcategoryVolumeById[sub._id] || 0;
        if (subVolume > 0) {
          subsForThisCategory.push({ id: sub._id, categoryId: cat._id, label: sub.label, value: subVolume });
        }
      });

      const matchedVolume = subsForThisCategory.reduce((sum, s) => sum + s.value, 0);
      const unmatchedVolume = (categoryVolumeById[cat._id] || 0) - matchedVolume;
      if (unmatchedVolume > 0.01) {
        subsForThisCategory.push({ id: `${cat._id}--other`, categoryId: cat._id, label: 'Autres', value: unmatchedVolume });
      }

      const categoryTotal = subsForThisCategory.reduce((sum, s) => sum + s.value, 0);
      if (categoryTotal <= 0) return;
      categoryTotals.push({ id: cat._id, label: cat.label, value: categoryTotal });
      subcategoryTotals.push(...subsForThisCategory);
    });

    if (uncategorizedVolume > 0.01) {
      categoryTotals.push({ id: 'uncategorized', label: 'Non catégorisé', value: uncategorizedVolume });
      subcategoryTotals.push({ id: 'uncategorized--other', categoryId: 'uncategorized', label: 'Non catégorisé', value: uncategorizedVolume });
    }

    return { categoryTotals, subcategoryTotals };
  }, [waterPointRows, taxonomyCategories, subcategoriesByCategory]);

  const categoryLabelById = useMemo(
    () => Object.fromEntries(categoryTotals.map((c) => [c.id, c.label])),
    [categoryTotals]
  );

  // Répartition par section (anneau intérieur) et pièce (anneau extérieur), au même
  // format que categoryTotals/subcategoryTotals pour réutiliser les cartes sunburst.
  // Une pièce répétée (nb_identical_rooms > 1) reste un seul arc, volume total des N
  // pièces (déjà inclus par computeAnnualVolumeM3), libellé « Nom ×N ». Dans chaque
  // section, les pièces sous ROOM_FOLD_SHARE du total du bâtiment sont regroupées en
  // « Autres pièces » (dès qu'il y en a au moins deux), dépliable dans la légende grâce
  // à roomKeys.
  const { sectionTotals, roomTotals } = useMemo(() => {
    const sections = {};
    let buildingTotal = 0;

    waterPointRows.forEach((row) => {
      const volume = row.annual_volume || 0;
      buildingTotal += volume;
      if (!sections[row.section_key]) {
        sections[row.section_key] = { id: row.section_key, label: row.name_section || 'Section inconnue', rooms: {} };
      }
      const rooms = sections[row.section_key].rooms;
      if (!rooms[row.room_key]) {
        rooms[row.room_key] = { key: row.room_key, label: row.name_room || 'Pièce inconnue', multiplier: row.room_multiplier, value: 0 };
      }
      rooms[row.room_key].value += volume;
    });

    const sectionTotals = [];
    const roomTotals = [];

    Object.values(sections).forEach((section) => {
      const rooms = Object.values(section.rooms)
        .filter((r) => r.value > 0)
        .sort((a, b) => b.value - a.value);
      const small = rooms.filter((r) => r.value < buildingTotal * ROOM_FOLD_SHARE);
      const kept = small.length >= 2 ? rooms.filter((r) => !small.includes(r)) : rooms;

      const children = kept.map((r) => ({
        id: r.key,
        categoryId: section.id,
        label: r.multiplier > 1 ? `${r.label} ×${r.multiplier}` : r.label,
        value: r.value,
        roomKeys: [r.key],
      }));
      if (small.length >= 2) {
        children.push({
          id: `${section.id}--other-rooms`,
          categoryId: section.id,
          label: `Autres pièces (${small.length})`,
          value: small.reduce((sum, r) => sum + r.value, 0),
          roomKeys: small.map((r) => r.key),
        });
      }

      const sectionTotal = children.reduce((sum, c) => sum + c.value, 0);
      if (sectionTotal <= 0) return;
      sectionTotals.push({ id: section.id, label: section.label, value: sectionTotal });
      roomTotals.push(...children);
    });

    // Sections par volume décroissant ; roomTotals suit le même ordre (les cartes
    // filtrent par categoryId, l'ordre interne d'une section est déjà décroissant).
    sectionTotals.sort((a, b) => b.value - a.value);
    const sectionOrder = Object.fromEntries(sectionTotals.map((s, i) => [s.id, i]));
    roomTotals.sort((a, b) => sectionOrder[a.categoryId] - sectionOrder[b.categoryId]);

    return { sectionTotals, roomTotals };
  }, [waterPointRows]);

  const getRowsForRoom = (sub, rows) => rows.filter((row) => sub.roomKeys.includes(row.room_key));

  // Une pièce seule : son nom est déjà sur la ligne de légende, on montre l'usage ;
  // « Autres pièces » : on préfixe le nom de la pièce.
  const roomRowSubtitle = (row, sub) =>
    sub.roomKeys.length > 1
      ? `${row.name_room || '-'} · ${row.usage_subcategory_label}`
      : row.usage_subcategory_label;

  // Le bucket "Autres" (reliquat non matché) existe une fois par catégorie et porte
  // toujours ce même libellé — s'il y en a plus d'un dans subcategoryTotals, toutes ses
  // occurrences doivent être désambiguïsées (qualifiées par leur catégorie parente) pour
  // rester uniques une fois aplaties en objet {label: value} ou comparées à un clic.
  const ambiguousSubcategoryLabels = useMemo(() => {
    const counts = {};
    subcategoryTotals.forEach((s) => {
      counts[s.label] = (counts[s.label] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((label) => counts[label] > 1));
  }, [subcategoryTotals]);

  // Aplati categoryTotals/subcategoryTotals (tableaux {id, label, value}) en objet
  // {label: value} pour alimenter les graphiques en donut (même format que
  // aggregateVolumeByFunctionalUnit). Qualifie les libellés de sous-catégorie ambigus
  // (cf. ambiguousSubcategoryLabels) pour ne jamais perdre de volume par collision de clé.
  const buildTaxonomyChartData = (totals) => {
    const result = {};
    totals.forEach((t) => {
      const label =
        t.categoryId && ambiguousSubcategoryLabels.has(t.label)
          ? `${t.label} (${categoryLabelById[t.categoryId] || ''})`
          : t.label;
      result[label] = (result[label] || 0) + t.value;
    });
    return result;
  };

  // Réplique la même règle de rattachement catégorie/sous-catégorie que le useMemo
  // categoryTotals/subcategoryTotals ci-dessus (id manquant ou catégorie/sous-catégorie
  // désactivée/renommée depuis la taxonomie → "Non catégorisé"/"Autres"), mais appliquée
  // point d'eau par point d'eau plutôt qu'agrégée — nécessaire pour filtrer le détail par
  // point d'eau (ConsumptionBreakdownDetail) sur les mêmes segments que ceux affichés
  // dans le donut/la légende. Si la règle de catégorisation change un jour, reporter le
  // changement ici aussi.
  const resolveRowTaxonomyLabels = (row) => {
    const category = row.usage_category_id
      ? taxonomyCategories.find((c) => c._id === row.usage_category_id)
      : null;
    if (!row.usage_subcategory_id || !category) {
      return { categoryLabel: 'Non catégorisé', subcategoryLabel: 'Non catégorisé' };
    }
    const subcategory = (subcategoriesByCategory[category._id] || []).find(
      (s) => s._id === row.usage_subcategory_id
    );
    const subcategoryLabel = subcategory ? subcategory.label : 'Autres';
    return {
      categoryLabel: category.label,
      subcategoryLabel: ambiguousSubcategoryLabels.has(subcategoryLabel)
        ? `${subcategoryLabel} (${category.label})`
        : subcategoryLabel,
    };
  };

  // Miroir de api/report_maker/report.py::consumption_per_unit (unit_id_to_name) : id
  // manquant ou unité supprimée depuis → "Non catégorisé", même bucket que celui déjà
  // utilisé côté back pour construire consumption_per_unit (qui alimente le graphique
  // "unité fonctionnelle"). `dataConsumption` porte directement le registre id→nom du
  // bâtiment (`functional_units`), pas besoin d'un fetch dédié.
  const functionalUnitNameById = useMemo(
    () => Object.fromEntries((dataConsumption?.functional_units || []).map((u) => [u.id, u.name])),
    [dataConsumption]
  );

  const resolveRowFunctionalUnitLabel = (row) =>
    (row.functional_unit && functionalUnitNameById[row.functional_unit]) || 'Non catégorisé';

  const enrichedWaterPointRows = useMemo(
    () =>
      waterPointRows.map((row) => {
        const { categoryLabel, subcategoryLabel } = resolveRowTaxonomyLabels(row);
        return {
          ...row,
          usage_category_label: categoryLabel,
          usage_subcategory_label: subcategoryLabel,
          functional_unit_label: resolveRowFunctionalUnitLabel(row),
          hot_water_ratio_effective: getEffectiveHotWaterRatio({
            usage: { hot_water_ratio: row.hot_water_ratio },
            equipment: {
              type_equipment: row.type_equipment,
              technical_data: { hot_water: row.hot_water_connected, hot_water_ratio: row.equipment_hot_water_ratio },
            },
          }),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [waterPointRows, taxonomyCategories, subcategoriesByCategory, ambiguousSubcategoryLabels, functionalUnitNameById]
  );

  // une sélection de segment dans une dimension (usage / unité fonctionnelle) n'a plus de sens si on change de dimension ou de type de donnée
  useEffect(() => {
    setSelectedSegment(null);
  }, [selectedFilter, dataType]);

  const handleSegmentClick = (label) => {
    setSelectedSegment((prev) => (prev === label ? null : label));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date inconnue";
  
    const months = [
      "janvier", "février", "mars", "avril", "mai", "juin",
      "juillet", "août", "septembre", "octobre", "novembre", "décembre"
    ];
  
    const [year, month, day] = dateString.split("-");
    return `${parseInt(day, 10)} ${months[parseInt(month, 10) - 1]} ${year}`;
  };

  useEffect(() => {
    const rawData =
      selectedFilter === 'usage'
        ? buildTaxonomyChartData(categoryTotals)
        : selectedFilter === 'subcategory'
        ? buildTaxonomyChartData(subcategoryTotals)
        : dataConsumption
        ? aggregateVolumeByFunctionalUnit(dataConsumption, dataType)
        : null;
    if (!rawData) return;

    const { labels, values } = foldIntoOthers(Object.keys(rawData), Object.values(rawData));
    const total = values.reduce((acc, value) => acc + value, 0);
    const colors = colorsAlongGradient(labels, CONSUMPTION_GRADIENT, (label) => label === 'Autres');
    const legend = generateLegendData(
      {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderColor: colors,
          },
        ],
      },
      total
    );
    setLegendData(legend);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataConsumption, selectedFilter, dataType, categoryTotals, subcategoryTotals, ambiguousSubcategoryLabels, categoryLabelById]);

  if (loading) return (
  <div className="centered-spinner-dashboard">
        <WalterLoader />
  </div>
  );
  if (error) return (
    <div className="dashboard-error-state">
      <FaExclamationTriangle className="dashboard-error-state-icon" />
      <h2 className="dashboard-error-state-title">Impossible d'afficher le tableau de bord</h2>
      <p className="dashboard-error-state-message">{error}</p>
    </div>
  );

  const totalConsumption = getReferenceConsumption(dataConsumption?.global_water_data) || 0;

  const costPerUse = dataConsumption?.cost_per_use

  const waterCosts = calculateWaterCosts(costPerUse);

  const selectedSegmentColor = legendData.find((item) => item.rawLabel === selectedSegment)?.borderColor;

  const consumptionChartData =
    selectedFilter === 'usage'
      ? buildTaxonomyChartData(categoryTotals)
      : selectedFilter === 'subcategory'
      ? buildTaxonomyChartData(subcategoryTotals)
      : aggregateVolumeByFunctionalUnit(dataConsumption, dataType);

  const consumptionChartTitles = {
    functional_unit: "Répartition par unité fonctionnelle",
    usage: 'Répartition par usage',
    subcategory: 'Répartition par sous-usage',
  };

  const roomSunburstModeToggle = (
    <SegmentedToggle
      ariaLabel="Mode d'affichage du graphique"
      value={roomSunburstMode}
      onChange={setRoomSunburstMode}
      options={[
        { value: 'interactive', label: 'Interactif' },
        { value: 'export', label: 'Export' },
      ]}
    />
  );

  const sunburstModeToggle = (
    <SegmentedToggle
      ariaLabel="Mode d'affichage du graphique"
      value={sunburstMode}
      onChange={setSunburstMode}
      options={[
        { value: 'interactive', label: 'Interactif' },
        { value: 'export', label: 'Export' },
      ]}
    />
  );

    return (
      <div className='building-dashboard-container'>
        <div className='building-dashboard-client-header'>
          <h1 className="building-dashboard-title">
            {dataConsumption?.client_summary?.client_name || "Inconnu"}
          </h1>

          <div className="dashboard-buttons">
            <button 
              className="back-button"
              onClick={() => navigate(`/client/${client_id}`)}
            >
              Retour vers dashboard client
            </button>

            <button 
              className="audit-button"
              onClick={() => navigate(`/audit/${client_id}/${building_id}`)}
            >
              Aller à l'audit
            </button>
          </div>
        </div>

        <div 
          className='building-dashboard-header'
          style={{ backgroundImage: `url(${dataConsumption?.image_url || '/background_test.jpg'})` }}
        >
          <div className='building-dashboard-header-gradient'></div>
          <div className='building-dashboard-building-name'>{dataConsumption?.name_building || "Inconnu"}</div>
          {dataConsumption?.audit_date && (
            <div className='building-dashboard-audit-date'>Analyse réalisée le {formatDate(dataConsumption.audit_date)}</div>
          )}
        </div>

        <div className="building-dashboard-buttons">
        <button onClick={() => navigate(`/client/${client_id}/${building_id}/action_plan_v2`)}>
          Modules de chiffrage
          </button>
          <button onClick={() => navigate(`/client/${building_id}/equipments`)}>
          Voir le détail de mes équipements
          </button>
          <button onClick={() => navigate(`/client/${building_id}/usages`)}>
          Voir le détail de mes usages
          </button>
          <button onClick={() => setShowReportPicker(true)}>
          Générer le rapport
          </button>
        </div>

        <div className="building-dashboard-encadres">
          <div className="building-dashboard-encadre">
            <h2>Caractéristiques du bâtiment</h2>
            <div className="building-dashboard-data">
                <FaMapMarkerAlt className="building-dashboard-encadre-icon" />
              <div className='building-dashboard-characteristics'>
                <strong>Adresse</strong>
                <div>{dataConsumption?.adress}</div>
                <div>{dataConsumption?.postal_code}, {dataConsumption?.city}</div>
              </div>
            </div>
            <div className="building-dashboard-data">
                <FaLayerGroup className="building-dashboard-encadre-icon" />
              <div className='building-dashboard-characteristics'>
                <strong>Surface</strong>
                <div>{formatValue(dataConsumption?.surface)} m²</div>
              </div>
            </div>
            <div className="building-dashboard-data">
                <FaBuilding className="building-dashboard-encadre-icon" />
              <div className='building-dashboard-characteristics'>
                <strong>Type de bâtiment</strong>
                <div>{buildingTypeLabels[dataConsumption?.type_building] || '-'}</div>
              </div>
            </div>
          </div>
          <div className="building-dashboard-encadre">
            <h2>Consommation annuelle</h2>
            <div className="building-dashboard-data">
            <div><strong>{formatValue(Math.round(totalConsumption))}</strong> m³/an</div>
            </div>
            <h2>Ratios</h2>
            <div className="building-dashboard-data">
              <FaUserAlt className="building-dashboard-encadre-icon" />
              <div>
              {totalConsumption / dataConsumption?.person_count < 1 
              ? <><strong>{formatValue((totalConsumption / dataConsumption?.person_count) * 1000)}</strong> L/pers</> 
              : <><strong>{formatValue(Math.round((1000 * totalConsumption)/ (dataConsumption?.person_count * 365.25)))}</strong> L/pers/jour</>}
              </div>
            </div>
            <div className="building-dashboard-data">
              <FaLayerGroup className="building-dashboard-encadre-icon" />
              <div>
                <><strong>{formatValue((totalConsumption / dataConsumption?.surface) * 1000)}</strong> L/m²</> 
              {/* {totalConsumption / dataConsumption?.surface < 1 
              ? <><strong>{formatValue((totalConsumption / dataConsumption?.surface) * 1000)}</strong> L/m²</> 
              : <><strong>{formatValue(Math.round(totalConsumption / dataConsumption?.surface))}</strong> m³/m²</>} */}
              </div>
            </div>
          </div>
          <div className="building-dashboard-encadre">
            <h2>Coût de l'eau</h2>
            <WaterCostBreakdown coldCost={waterCosts.coldWaterCost} hotCost={waterCosts.hotWaterCost} />
          </div>
        </div>
      
        <div className="building-dashboard-graphs">
          <div className="building-dashboard-graphs-header">
            <h2>Répartition des consommations</h2>
            <SegmentedToggle
              ariaLabel="Mode d'affichage du graphique"
              value={chartMode}
              onChange={setChartMode}
              options={[
                { value: 'interactive', label: 'Interactif' },
                { value: 'export', label: 'Export' },
              ]}
            />
          </div>

          <div className="chart-settings-bar">
            <div className='building-dashboard-consumption-type'>
              <p>Afficher :</p>
              <SegmentedToggle
                variant="primary"
                ariaLabel="Type de donnée affichée"
                value={dataType}
                onChange={setDataType}
                options={[
                  { value: 'real', label: 'Réel' },
                  { value: 'estimated', label: 'Estimé' },
                ]}
              />
            </div>
            <div className='building-dashboard-graph-filter'>
              <p>Répartir par</p>
              <SegmentedToggle
                variant="primary"
                ariaLabel="Répartir par"
                value={selectedFilter}
                onChange={setSelectedFilter}
                options={[
                  { value: 'functional_unit', label: 'Unité fonctionnelle' },
                  { value: 'usage', label: 'Usage' },
                  { value: 'subcategory', label: 'Sous-usage' },
                ]}
              />
            </div>
          </div>

          {chartMode === 'interactive' ? (
            <div className="building-dashboard-graphs-chart-row">
              <div className="building-dashboard-graph">
                <UsageDonutChartNivo
                  data={consumptionChartData}
                  unit="m³"
                  onSegmentClick={handleSegmentClick}
                  selectedLabel={selectedSegment}
                />
              </div>
              <div className="building-dashboard-graph-control">
                <div className="building-dashboard-graph-legend-header">
                  <h3>Légende</h3>
                </div>
                <div className='building-dashboard-graph-legend'>
                  {legendData.map((item, index) => (
                    <div
                      key={index}
                      className={`legend-item${item.rawLabel === selectedSegment ? ' legend-item--active' : ''}`}
                      onClick={() => handleSegmentClick(item.rawLabel)}
                    >
                      <span
                        className="legend-color"
                        style={{
                          backgroundColor: item.backgroundColor,
                          border: `1px solid ${item.borderColor}`,
                        }}
                      ></span>
                      <span className="legend-label">{item.label}</span>
                    </div>
                  ))}
                </div>

                {selectedSegment && (
                  <ConsumptionBreakdownDetail
                    rows={enrichedWaterPointRows}
                    loading={waterPointsLoading}
                    error={waterPointsError}
                    selectedSegment={selectedSegment}
                    selectedFilter={selectedFilter}
                    segmentColor={selectedSegmentColor}
                    onClose={() => setSelectedSegment(null)}
                  />
                )}
              </div>
            </div>
          ) : (
            <UsageDonutExportView
              data={consumptionChartData}
              unit="m³"
              title={consumptionChartTitles[selectedFilter]}
            />
          )}
        </div>

        {categoryTotals.length > 0 && sunburstMode === 'interactive' && (
          <UsageTaxonomySunburstCard
            categoryTotals={categoryTotals}
            subcategoryTotals={subcategoryTotals}
            waterPointRows={enrichedWaterPointRows}
            unit="m³"
            headerActions={sunburstModeToggle}
          />
        )}

        {categoryTotals.length > 0 && sunburstMode === 'export' && (
          <UsageTaxonomySunburstReportCard
            categoryTotals={categoryTotals}
            subcategoryTotals={subcategoryTotals}
            unit="m³"
            headerActions={sunburstModeToggle}
          />
        )}

        {sectionTotals.length > 0 && roomSunburstMode === 'interactive' && (
          <UsageTaxonomySunburstCard
            title="Répartition par section et pièce"
            innerEyebrow="Section"
            categoryTotals={sectionTotals}
            subcategoryTotals={roomTotals}
            waterPointRows={enrichedWaterPointRows}
            getRowsForSub={getRowsForRoom}
            rowSubtitle={roomRowSubtitle}
            unit="m³"
            headerActions={roomSunburstModeToggle}
          />
        )}

        {sectionTotals.length > 0 && roomSunburstMode === 'export' && (
          <UsageTaxonomySunburstReportCard
            title="Répartition par section et pièce"
            fileName="repartition-par-section-piece-rapport.svg"
            terms={{ inner: 'sections', outer: 'pièces' }}
            categoryTotals={sectionTotals}
            subcategoryTotals={roomTotals}
            unit="m³"
            headerActions={roomSunburstModeToggle}
          />
        )}

        <CostPerUsageCard
          waterPointRows={enrichedWaterPointRows}
          coldPrice={dataConsumption?.global_water_data?.water_price}
          hotPrice={dataConsumption?.global_water_data?.hot_water_price}
        />

        <ConsumptionPerPersonCard
          categoryTotals={categoryTotals}
          subcategoryTotals={subcategoryTotals}
          personCount={dataConsumption?.person_count}
        />
         <div className='building-dashboard-encadres'>
            <div className="building-dashboard-encadre">
              <h2>Note par équipements</h2>
              <RatePerEquipment data={dataConsumption?.rate_per_equipment} />
            </div>
          </div>
          <RatePerRoomCard data={dataConsumption?.consumption_per_room} />


        {/* Nouvelle section risques */}
        <div className='building-dashboard-encadres'>
          <div className="building-dashboard-encadre">
            <h2>Risques du bâtiment</h2>
              <div>
              {[
                { name: "Inondations", icon: "/icons/flood_icon.png", risk: dataConsumption?.risks?.inondation },
                { name: "Risques côtiers (submersion marine, tsunami)", icon: "/icons/tsunami_icon.png", risk: dataConsumption?.risks?.risqueCotier },
                { name: "Recul du trait de côte", icon: "/icons/coast_erosion_icon.png", risk: dataConsumption?.risks?.reculTraitCote },
                { name: "Retrait gonflement des argiles", icon: "/icons/argile_icon.png", risk: dataConsumption?.risks?.retraitGonflementArgile }
              ]
              .map((item) => ({ ...item, level: riskLevel(item.risk) }))
              .sort((a, b) => RISK_LEVELS[a.level].order - RISK_LEVELS[b.level].order)
              .map((item, index) => (
                <div key={index} className="building-dashboard-data">
                  <img src={item.icon} alt={item.name} title={item.name} className="building-dashboard-risk-icon" />
                  <div className='building-dashboard-characteristics'>
                    <strong>{item.name}</strong>
                    <span className={`building-dashboard-risk-pill building-dashboard-risk-pill--${RISK_LEVELS[item.level].color}`}>
                      <span className="building-dashboard-risk-dot" />
                      {RISK_LEVELS[item.level].label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="building-dashboard-encadre">
            <h2>Impact grand cycle de l'eau (CBS)</h2>
            {dataConsumption?.cbs && (
              <div className="building-dashboard-gauge-container">
              <Gauge value={dataConsumption.cbs} />
              </div>
            )}
            {dataConsumption?.cbs_detail && dataConsumption.cbs_detail.cbs === dataConsumption.cbs && (
              <p className='text-cbs'>
                CBS potentiel en désimperméabilisant les surfaces imperméables :{' '}
                <strong>{Number(dataConsumption.cbs_detail.cbs_potential).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                <br />
                Calculé sur {Math.round(dataConsumption.cbs_detail.surface_m2).toLocaleString('fr-FR')} m² de parcelle
                (cadastre et CoSIA {(dataConsumption.cbs_detail.cosia_layers || []).join(', ').replace(/IGNF_COSIA_/g, '')}, IGN).
              </p>
            )}
            {!dataConsumption?.cbs && (
              <div className="building-dashboard-gauge-container">
              Pas de données
              </div>
            )}
            <h2>Qu'est-ce que le CBS ?</h2>
            <p className='text-cbs'>
              Le CBS (coefficient de biotope par surface) est défini par l'ADEME comme la proportion des surfaces favorables à la biodiversité, dite éco-aménageables, par rapport à la surface totale d’une parcelle. 
              Plus il est élevé, plus les conséquences négatives de l'aménagement sur l’environnement sont limitées. <br /> <br />
              Un CBS très faible compromet l’infiltration et la filtration de l’eau dans les sols, ce qui augmente la quantité de l’eau de ruissellement tout en altérant sa qualité. 
              En cas de grandes précipitations, cela augmente le débit des crues. <br /> <br />
              Pour le calculer, on calcule la somme des surfaces éco-aménagées pondérées par un coefficient et on divise cette somme par la surface totale.
              
            </p>
            <div className="building-dashboard-cbs-info-coeff">
              <p>Voir le détail des coefficients</p>
              <FaInfoCircle className="building-dashboard-info-button" />
              <img
                src="/cbs_coeff.png"
                alt="Tableau des coefficients"
                className="building-dashboard-info-image"
              />
            </div>
          </div>
        </div>
        <div className='building-dashboard-encadres'>
          <div className="building-dashboard-encadre">
            <h2>Économies par mesure</h2>
            <SavingsPerMeasure data={dataConsumption?.measures} />
          </div>
        </div>
        <ToastContainer />
        {showReportPicker && (
          <ReportFolderPickerModal
            buildingId={building_id}
            onClose={() => setShowReportPicker(false)}
          />
        )}
      </div>
    );

};

export default ClientBuildingDashboard;
